import {inject, Injectable, signal} from '@angular/core';
import {firstValueFrom} from "rxjs";
import {HttpErrorResponse} from "@angular/common/http";
import * as Sentry from "@sentry/angular";
import {ExternalApiService} from "../../core/api/external-api/external-api.service";
import {AnalysisType} from "../../core/models/analysis";
import {isTyre, PictureKind, pictureToFile, StoredPicture, UploadState} from "../../core/models/stored-picture";
import {PictureStoreService} from "./picture-store.service";

export interface UploadProgress {
  total: number;
  uploaded: number;
  pending: number;
  failed: number;
}

export const ALREADY_UPLOADED_ERROR = 'PICTURE_ALREADY_UPLOADED_EXCEPTION';

const EMPTY_PROGRESS: UploadProgress = {total: 0, uploaded: 0, pending: 0, failed: 0};

const sameProgress = (a: UploadProgress, b: UploadProgress) =>
  a.total === b.total && a.uploaded === b.uploaded && a.pending === b.pending && a.failed === b.failed;

@Injectable({
  providedIn: 'root'
})
export class UploadQueueService {

  private store = inject(PictureStoreService);
  private externalApi = inject(ExternalApiService);

  private readonly maxAttempts = 3;
  private readonly baseBackoffMs = 500;
  private currentRun?: Promise<void>;
  private readonly _progress = signal<UploadProgress>(EMPTY_PROGRESS, {equal: sameProgress});

  readonly progress = this._progress.asReadonly();

  async enqueue(picture: StoredPicture): Promise<void> {
    await this.store.put({...picture, uploadState: UploadState.PENDING, attempts: 0, lastError: undefined});
    await this.refreshProgress();
    this.processInBackground();
  }

  async retryFailed(): Promise<void> {
    const failed = await this.store.byState(UploadState.FAILED);
    for (const picture of failed) {
      await this.store.patch(picture.key, {uploadState: UploadState.PENDING, attempts: 0, lastError: undefined});
    }
    await this.refreshProgress();
    this.processInBackground();
  }

  async resume(): Promise<void> {
    const stale = await this.store.byState(UploadState.UPLOADING);
    for (const picture of stale) {
      await this.store.markPending(picture.key);
    }
    await this.refreshProgress();
    this.processInBackground();
  }

  async refreshProgress(): Promise<UploadProgress> {
    const pictures = await this.store.all();
    const progress: UploadProgress = {
      total: pictures.length,
      uploaded: pictures.filter(p => p.uploadState === UploadState.UPLOADED).length,
      pending: pictures.filter(p => p.uploadState === UploadState.PENDING || p.uploadState === UploadState.UPLOADING).length,
      failed: pictures.filter(p => p.uploadState === UploadState.FAILED).length
    };
    this._progress.set(progress);
    return progress;
  }

  async waitUntilIdle(): Promise<UploadProgress> {
    await this.ensureProcessing();
    const queuedMeanwhile = await this.store.nextPending();
    return queuedMeanwhile ? this.waitUntilIdle() : this.refreshProgress();
  }

  private processInBackground(): void {
    this.ensureProcessing().catch((error) => {
      Sentry.captureException(new Error('Upload queue stopped'), {extra: {error}});
      void this.refreshProgress();
    });
  }

  private ensureProcessing(): Promise<void> {
    if (!this.currentRun) {
      this.currentRun = this.processPendingPictures().finally(() => this.currentRun = undefined);
    }
    return this.currentRun;
  }

  private async processPendingPictures(): Promise<void> {
    const picture = await this.store.nextPending();
    if (!picture) {
      return;
    }
    await this.processPicture(picture);
    await this.refreshProgress();
    return this.processPendingPictures();
  }

  private async processPicture(picture: StoredPicture): Promise<void> {
    await this.store.markUploading(picture.key);
    try {
      await this.sendToServer(picture);
      if (await this.hasNotBeenRetaken(picture)) {
        await this.store.markUploaded(picture.key);
      }
    } catch (error) {
      if (await this.hasNotBeenRetaken(picture)) {
        await this.handleFailure(picture, error);
      }
    }
  }

  private async hasNotBeenRetaken(picture: StoredPicture): Promise<boolean> {
    const current = await this.store.get(picture.key);
    return !!current && current.capturedAt === picture.capturedAt;
  }

  private async handleFailure(picture: StoredPicture, error: unknown): Promise<void> {
    if (this.isAlreadyUploaded(error)) {
      await this.store.markUploaded(picture.key);
      return;
    }
    const attempts = (picture.attempts ?? 0) + 1;
    if (this.isRetryable(error) && attempts < this.maxAttempts) {
      await this.delay(this.baseBackoffMs * Math.pow(2, attempts - 1));
      await this.store.markPending(picture.key);
      return;
    }
    Sentry.captureMessage('Picture upload failed', {
      extra: {key: picture.key, kind: picture.kind, analysisId: picture.analysisId, attempts, error}
    });
    await this.store.markFailed(picture.key, this.describe(error));
  }

  private sendToServer(picture: StoredPicture): Promise<unknown> {
    const file = pictureToFile(picture);
    if (isTyre(picture)) {
      return firstValueFrom(this.externalApi.uploadTyrePicture(picture.analysisId, picture.step.type, picture.step.position, file));
    }
    const analysisType = picture.kind === PictureKind.VIN ? AnalysisType.VIN : AnalysisType.REGISTRATION_CARD;
    return firstValueFrom(this.externalApi.createVehicleAnalysis(picture.analysisId, analysisType, file));
  }

  private isAlreadyUploaded(error: unknown): boolean {
    return error instanceof HttpErrorResponse
      && error.status === 400
      && error.error?.error === ALREADY_UPLOADED_ERROR;
  }

  private isRetryable(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }
    return error.status === 0 || error.status === 408 || error.status === 429 || error.status >= 500;
  }

  private describe(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      return `${error.status} ${error.statusText}`;
    }
    return error instanceof Error ? error.message : String(error);
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
